import axios, { AxiosInstance } from 'axios';

import { BaseUrl, IdentityBaseUrl } from '../constants';
import { isJwtExpired } from '../helpers/isJwtExpired';
import { AuthResponse } from '../types/interfaces/AuthResponse';

let refreshTokenPromise: Promise<string | null> | null = null;

async function refreshTokenSynchronized(): Promise<string | null> {
  if (refreshTokenPromise) {
    return refreshTokenPromise;
  }

  refreshTokenPromise = (async () => {
    try {
      const response = await axios.post<AuthResponse>(
        '/refresh',
        undefined,
        {
          withCredentials: true,
          baseURL: IdentityBaseUrl
        }
      );
      if (response.data.accessToken) {
        localStorage.setItem('token', response.data.accessToken);
        return response.data.accessToken;
      }
      return null;
    } catch (e) {
      localStorage.removeItem('token');
      const loginUrl = '/login';
      if (window.location.pathname !== loginUrl)
        window.location.href = '/login';
      return null;
    } finally {
      refreshTokenPromise = null;
    }
  })();

  return refreshTokenPromise;
}

function configureApi(api: AxiosInstance, withToken: boolean): void {
  if (withToken) {
    api.interceptors.request.use(async (config) => {
      const token = localStorage.getItem('token');
      
      if (token && isJwtExpired(token)) {
        const newToken = await refreshTokenSynchronized();
        if (newToken && config.headers !== undefined) {
          config.headers.Authorization = `Bearer ${newToken}`;
        }
      } else if (config.headers !== undefined && token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      
      return config;
    });
  }

  api.interceptors.response.use(
    (config) => {
      return config;
    },
    async (error) => {
      const originalRequest = error.config;
      if (
        error.message !== 'canceled' &&
        error.response?.status === 401 &&
        error.config &&
        !error.config._isRetry
      ) {
        originalRequest._isRetry = true;
        try {
          const newToken = await refreshTokenSynchronized();
          if (newToken) {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
            }
            return api.request(originalRequest);
          }
        } catch (e) {
          // Ошибка уже обработана в refreshTokenSynchronized
        }
      }
      return error.response;
    }
  );
}

export const api = axios.create({
  withCredentials: true,
  baseURL: BaseUrl
});
configureApi(api, true);

export const authApi = axios.create({
  withCredentials: true,
  baseURL: IdentityBaseUrl
});
configureApi(authApi, true);
